import styled from "styled-components";
import mainBg from "@/assets/images/main-background.png";
import { radius } from "@libertify/ui";

// ─── Layout ──────────────────────────────────────────────────────────────────

export const PagePW = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  height: 100vh;
  overflow: hidden;
  font-family: "Inter Variable", "Inter", sans-serif;
  background-image: url(${mainBg});
  background-size: cover;
  background-position: center;

  @media (width <= 768px) {
    flex-direction: column;
    overflow-y: auto;
  }
`;

export const LeftColPW = styled.div`
  flex: 0 0 55%;
  padding: clamp(40px, 6vh, 72px) clamp(40px, 5vw, 72px);
  overflow-y: auto;

  @media (width <= 768px) {
    flex: none;
    width: 100%;
    padding: 32px 20px 24px;
    overflow-y: visible;
  }
`;

export const RightColPW = styled.div`
  flex: 0 0 45%;
  padding: clamp(40px, 6vh, 72px) clamp(32px, 4vw, 56px);
  display: flex;
  align-items: stretch;
  justify-content: center;
  overflow-y: auto;

  @media (width <= 768px) {
    flex: none;
    width: 100%;
    padding: 0 20px 48px;
    align-items: stretch;
    overflow-y: visible;
  }
`;

// ─── Left panel ──────────────────────────────────────────────────────────────

export const LogoBarPW = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: clamp(32px, 5vh, 56px);

  svg {
    height: 32px;
    width: auto;
  }
`;

export const EyebrowPW = styled.span`
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  color: #ffffff;
  background: #00000080;
  border-radius: ${radius.pill}px;
  padding: 4px 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 16px;
`;

export const HeroPW = styled.h1`
  font-family: "Poppins", "Inter Variable", sans-serif;
  font-size: clamp(36px, 4vw, 56px);
  font-weight: 700;
  color: #090e19;
  letter-spacing: -0.03em;
  line-height: 1.05;
  margin: 0 0 16px;
`;

export const HeroSubPW = styled.p`
  font-size: 16px;
  color: #6b7280;
  line-height: 1.6;
  margin: 0 0 clamp(32px, 5vh, 52px);
  max-width: 500px;
`;

// ─── Timeline ────────────────────────────────────────────────────────────────

export const TimelineGridPW = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 14px;
  margin-bottom: clamp(40px, 6vh, 60px);

  @media (width <= 640px) {
    grid-template-columns: 1fr;
  }
`;

export const TimelineCardPW = styled.div<{ $accent: string }>`
  background: white;
  border: 1px solid #e5e7eb;
  border-left: 4px solid ${(p) => p.$accent};
  border-radius: ${radius.box}px;
  padding: 20px 18px;
`;

export const DayBadgePW = styled.span<{ $accent: string }>`
  font-size: 11px;
  font-weight: 700;
  color: ${(p) => p.$accent};
  text-transform: uppercase;
  letter-spacing: 0.07em;
  display: block;
  margin-bottom: 8px;
`;

export const TimelineTitlePW = styled.p`
  font-size: 14px;
  font-weight: 700;
  color: #090e19;
  margin: 0 0 6px;
`;

export const TimelineDescPW = styled.p`
  font-size: 13px;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
`;

// ─── Features section ────────────────────────────────────────────────────────

export const FeaturesSectionTitlePW = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #090e19;
  margin: 0 0 20px;
  letter-spacing: -0.01em;
`;

export const FeaturesGridPW = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 24px;

  @media (width <= 480px) {
    grid-template-columns: 1fr;
  }
`;

export const FeaturesColPW = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const FeatureItemPW = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 14px;
  color: #374151;
  line-height: 1.45;

  svg {
    flex-shrink: 0;
    margin-top: 1px;
  }
`;

// ─── Top bar ────────────────────────────────────────────────────────────────

export const TopBarPW = styled.div`
  position: absolute;
  top: 24px;
  right: 32px;
  display: flex;
  align-items: center;
  gap: 4px;
  z-index: 10;
  color: #164f4b;

  .ant-btn {
    color: #164f4b;
  }

  @media (width <= 768px) {
    top: 16px;
    right: 16px;
  }
`;

// ─── Right column — sticky card ─────────────────────────────────────────────

export const StickyWrapPW = styled.div`
  width: 100%;
  max-width: 360px;
  display: flex;
  flex-direction: column;
  margin-top: 110px;

  @media (width <= 768px) {
    margin-top: 0;
    max-width: 100%;
  }
`;

export const PricingCardPW = styled.div`
  flex: 1;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: ${radius.box}px;
  padding: 32px 28px;
  box-shadow: 0 4px 24px -8px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 610px;
`;

// ─── Out-of-control variant ─────────────────────────────────────────────────

export const HeroImagePW = styled.div`
  width: 100%;
  max-width: 480px;
  height: 200px;
  border-radius: ${radius.box}px;
  background: linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%);
  margin-bottom: 24px;

  @media (width <= 768px) {
    height: 160px;
  }
`;

export const FeaturesCardPW = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: ${radius.box}px;
  padding: 28px 24px;
  margin-bottom: 24px;
  box-shadow: 0 2px 12px -4px rgba(0, 0, 0, 0.06);
`;

export const CtaDescriptionPW = styled.p`
  font-size: 14px;
  color: #6b7280;
  line-height: 1.6;
  margin: 0;
  max-width: 500px;
`;

export const CtaButtonPW = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 16px 32px;
  margin-top: 24px;
  background: #14b8a6;
  color: #ffffff;
  font-size: 16px;
  font-weight: 700;
  border-radius: ${radius.pill}px;
  cursor: pointer;
  transition: background 0.15s ease;

  &::before {
    content: "⚡";
    margin-right: 8px;
  }

  &:hover {
    background: #0d9488;
  }
`;

export const CtaSubtextPW = styled.p`
  text-align: center;
  font-size: 13px;
  color: #6b7280;
  margin: 12px 0 0;
  text-decoration: underline;
  cursor: pointer;
`;
